import React, { useState } from "react";
import { Grid, Button, MenuItem, FormControl, InputLabel, Select, CircularProgress, Stack } from "@mui/material";
import CustomSelect from "../../components/CustomSelect";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EmailIcon from '@mui/icons-material/Email';
import { api } from "../../helpers/apiRequest";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import colors from "../../theme/Colors";
import { exportToPDF, exportToExcel, getPDFBlob } from "./ReportUtils";
import EmailReportDialog from "../EmailReportDialog";

const GstSaleReport = ({ customers }) => {
  const [loadingGstSale, setLoadingGstSale] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmailCustomer, setSelectedEmailCustomer] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [gstSaleFilters, setGstSaleFilters] = useState({
    customer: "",
    dateFrom: null,
    dateTo: null,
  });

  const generateGstSaleReport = async (exportType = 'html') => {
    const params = new URLSearchParams();
    
    if (gstSaleFilters.customer) {
      params.append('customer', gstSaleFilters.customer);
    }
    
    if (gstSaleFilters.dateFrom) {
      params.append('dateFrom', gstSaleFilters.dateFrom.format("YYYY-MM-DD"));
    }
    
    if (gstSaleFilters.dateTo) {
      params.append('dateTo', gstSaleFilters.dateTo.format("YYYY-MM-DD"));
    }
    
    const queryString = params.toString();
    
    setLoadingGstSale(true);
    try {
      const response = await api.get(
        `/reports/gst-sale-report${queryString ? `?${queryString}` : ''}`
      );
      
      if (response.data && response.data.status) {
        const totalAmount = response.data.report.reduce((sum, order) => sum + (Number(order.total_price) || 0), 0);
        const totalGst = response.data.report.reduce((sum, order) => sum + (Number(order.gst) || 0), 0);
        const totalQst = response.data.report.reduce((sum, order) => sum + (Number(order.qst) || 0), 0);
        const totalHst = response.data.report.reduce((sum, order) => sum + (Number(order.hst) || 0), 0);
        const totalFinal = response.data.report.reduce((sum, order) => sum + (Number(order.final_price) || 0), 0);

        const customerName = gstSaleFilters.customer 
          ? customers.find(c => c._id === gstSaleFilters.customer)?.name || "Unknown" 
          : "All Customers";
        const dateFromStr = gstSaleFilters.dateFrom ? gstSaleFilters.dateFrom.format("YYYY-MM-DD") : "All";
        const dateToStr = gstSaleFilters.dateTo ? gstSaleFilters.dateTo.format("YYYY-MM-DD") : "All";
        
        if (exportType === 'excel') {
          const excelData = response.data.report.map(order => ({
            "Date": order.date,
            "Sheet #":order?.sh_no,
            "Sheet #":order?.invoice_no,

            "Order Number": order.order_number,
            "Customer": order.customer_name,
            "Total Price": order.total_price,
            "GST": order.gst,
            "QST": order.qst,
            "HST": order.hst,
            "Final Price": order.final_price
          }));
          
          exportToExcel(excelData, `GST_Sale_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("Excel report generated successfully");
          return;
        }
        
        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Invoice Sale Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 15px; padding: 50px 30px; align-items:center;-webkit-user-select: text;-moz-user-select: text;-ms-user-select: text; user-select: text;  }
             h1 { color: #1abc9c; text-align: center; }
              table { border-collapse: collapse; width: 95%; margin: 15px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              .t {margin:15px;}
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin: 20px; }
              .report-filters { margin: 15px; font-size: 14px; }
              .print-btn { background-color: #1abc9c; color: white; border: none; padding: 10px 20px; 
                          cursor: pointer; border-radius: 4px; margin-top: 20px; margin-right: 10px; }
              .export-btn-container { display: flex; margin-top: 20px; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Invoice Sale Report</h1>
              <div class="report-filters">
                <p><strong>Customer:</strong> ${customerName}</p>
                <p><strong>Date Range:</strong> ${dateFromStr} to ${dateToStr}</p>
              </div>
              <p class="t">Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
            </div>
            
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th>${totalAmount.toFixed(2)}</th>
                  <th>${totalGst.toFixed(2)}</th>
                  <th>${totalQst.toFixed(2)}</th>
                  <th>${totalHst.toFixed(2)}</th>
                  <th>${totalFinal.toFixed(2)}</th>
                </tr>
                <tr>
                  <th>Date</th>
                  <th>Sheet #</th>
                  <th>Invoice #</th>
                  
                  
                  <th>Customer</th>
                  <th>Amount</th>
                  <th>GST</th>
                  <th>QST</th>
                  <th>HST</th>
                  
                  <th>Total Amount</th>
                </tr>
              </thead>
              <tbody>
        `;
        
        response.data.report.forEach(order => {
          htmlContent += `
            <tr>
              <td>${order.date}</td>
              <td>${order?.sh_no || 'N/A'}</td>
              <td>${order?.invoice_no || 'N/A'}</td>

              
              
              <td>${order.customer_name}</td>
              <td>${order.total_price}</td>
              <td>${order.gst}</td>
              <td>${order.qst}</td>
              <td>${order.hst}</td>
              
              <td>${order.final_price}</td>
            </tr>
          `;
        });
        
        htmlContent += `
              </tbody>
            </table>
            
            <div class="export-btn-container">
            </div>
          </body>
          </html>
        `;
        
        if (exportType === 'pdf') {
          exportToPDF(htmlContent, `GST_Sale_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("PDF report generated successfully");
          return;
        }
        
        if (exportType === 'email') {
          setEmailDialogOpen(true);
          setLoadingGstSale(false);
          return;
        }
        
        const reportWindow = window.open('', '_blank');
        if (!reportWindow) {
          toast.error("Please allow popups for this site to view reports");
          return;
        }
        
        reportWindow.document.write(htmlContent);
        reportWindow.document.close();
        
        window.addEventListener('message', function(event) {
          if (event.data.type === 'exportPDF' && event.data.reportType === 'gstSale') {
            generateGstSaleReport('pdf');
          } else if (event.data.type === 'exportExcel' && event.data.reportType === 'gstSale') {
            generateGstSaleReport('excel');
          }
        });
        
        toast.success("Report generated successfully");
      } else {
        toast.error("No data available for the selected filters");
      }
    } catch (error) {
      console.error("Error generating order report:", error);
      toast.error("Failed to generate order report");
    } finally {
      setLoadingGstSale(false);
    }
  };

  const sendReportByEmail = async () => {
    if (!selectedEmailCustomer) {
      toast.error('Please select a customer');
      return;
    }

    setSendingEmail(true);
    try {
      // Generate the report HTML
      const params = new URLSearchParams();
      if (gstSaleFilters.customer) params.append('customer', gstSaleFilters.customer);
      if (gstSaleFilters.dateFrom) params.append('dateFrom', gstSaleFilters.dateFrom.format("YYYY-MM-DD"));
      if (gstSaleFilters.dateTo) params.append('dateTo', gstSaleFilters.dateTo.format("YYYY-MM-DD"));
      
      const queryString = params.toString();
      
      const response = await api.get(`/reports/gst-sale-report${queryString ? `?${queryString}` : ''}`);
      
      if (response.data && response.data.status) {
        const totalAmount = response.data.report.reduce((sum, order) => sum + (Number(order.total_price) || 0), 0);
        const totalGst = response.data.report.reduce((sum, order) => sum + (Number(order.gst) || 0), 0);
        const totalQst = response.data.report.reduce((sum, order) => sum + (Number(order.qst) || 0), 0);
        const totalHst = response.data.report.reduce((sum, order) => sum + (Number(order.hst) || 0), 0);
        const totalFinal = response.data.report.reduce((sum, order) => sum + (Number(order.final_price) || 0), 0);

        const customerName = gstSaleFilters.customer 
          ? customers.find(c => c._id === gstSaleFilters.customer)?.name || "Unknown" 
          : "All Customers";
        const dateFromStr = gstSaleFilters.dateFrom ? gstSaleFilters.dateFrom.format("YYYY-MM-DD") : "All";
        const dateToStr = gstSaleFilters.dateTo ? gstSaleFilters.dateTo.format("YYYY-MM-DD") : "All";
        
        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Invoice Sale Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              h1 { color: #1abc9c; }
              table { border-collapse: collapse; width: 100%; margin-top: 20px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin-bottom: 20px; }
              .report-filters { margin-bottom: 15px; font-size: 14px; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Invoice Sale Report</h1>
              <div class="report-filters">
                <p><strong>Customer:</strong> ${customerName}</p>
                <p><strong>Date Range:</strong> ${dateFromStr} to ${dateToStr}</p>
              </div>
              <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
            </div>
            
             <table>
              <thead>
                <tr>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th>${totalAmount.toFixed(2)}</th>
                  <th>${totalGst.toFixed(2)}</th>
                  <th>${totalQst.toFixed(2)}</th>
                  <th>${totalHst.toFixed(2)}</th>
                  <th>${totalFinal.toFixed(2)}</th>
                </tr>
                <tr>
                  <th>Date</th>
                  <th>Sheet #</th>
                  <th>Invoice #</th>
                 
                 
                  <th>Customer</th>
                  <th>Amount</th>
                  <th>GST</th>
                  <th>QST</th>
                  <th>HST</th>
                  
                  <th>Total Amount</th>
                </tr>
              </thead>
              <tbody>
        `;
        
        response.data.report.forEach(order => {
          htmlContent += `
            <tr>
              <td>${order.date}</td>
              <td>${order?.sh_no || 'N/A'}</td>
              <td>${order?.invoice_no || 'N/A'}</td>

              <td>${order.order_number}</td>
             
              <td>${order.customer_name}</td>
              <td>${order.total_price}</td>
              <td>${order.gst}</td>
              <td>${order.qst}</td>
              <td>${order.hst}</td>
              
              <td>${order.final_price}</td>
            </tr>
          `;
        });
        
        htmlContent += `
              </tbody>
            </table>
          </body>
          </html>
        `;
        
        // Convert HTML to PDF blob
        const pdfBlob = await getPDFBlob(htmlContent);
        
        // Create FormData and append the PDF blob
        const formData = new FormData();
        formData.append('image', pdfBlob, `GST_Sale_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.pdf`);
        formData.append('userId', selectedEmailCustomer);
        formData.append('type', "Report");
        // Send the email
        const emailResponse = await api.post('inventory/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (emailResponse.status === 200) {
          toast.success('Report sent by email successfully');
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        } else {
          toast.error('Failed to send report by email');
        }
      } else {
        toast.error('No data available for the selected filters');
      }
    } catch (error) {
      console.error('Error sending report by email:', error);
      toast.error('Failed to send report by email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Grid container spacing={3} alignItems="center">
      <Grid item xs={12} sm={4}>
        <CustomSelect
          label="Customer"
          value={gstSaleFilters.customer}
          onChange={(e) => setGstSaleFilters({ ...gstSaleFilters, customer: e.target.value })}
          options={[
            { id: "", name: "All Customers" },
            ...customers.map((customer) => ({ id: customer._id, name: customer.name })),
          ]}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <DatePicker
          label="From Date"
          value={gstSaleFilters.dateFrom}
          onChange={(newValue) => setGstSaleFilters({ ...gstSaleFilters, dateFrom: newValue })}
          slotProps={{ textField: { fullWidth: true } }}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <DatePicker
          label="To Date"
          value={gstSaleFilters.dateTo}
          onChange={(newValue) => setGstSaleFilters({ ...gstSaleFilters, dateTo: newValue })}
          slotProps={{ textField: { fullWidth: true } }}
        />
      </Grid>
      <Grid item xs={12} sm={2}>
        <Stack spacing={1}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => generateGstSaleReport('html')}
            disabled={loadingGstSale || sendingEmail}
            startIcon={<DescriptionIcon />}
            sx={{ 
              height: "38px", 
              backgroundColor: colors.primary, 
              '&:hover': { 
                backgroundColor: colors.primary, 
                opacity: 0.9 
              } 
            }}
          >
            {loadingGstSale ? <CircularProgress size={24} color="inherit" /> : "View"}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateGstSaleReport('pdf')}
            disabled={loadingGstSale || sendingEmail}
            startIcon={<PictureAsPdfIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            PDF
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateGstSaleReport('excel')}
            disabled={loadingGstSale || sendingEmail}
            startIcon={<FileDownloadIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateGstSaleReport('email')}
            disabled={loadingGstSale || sendingEmail}
            startIcon={<EmailIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            Email
          </Button>
        </Stack>
      </Grid>

      <EmailReportDialog
        open={emailDialogOpen}
        handleClose={() => {
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        }}
        selectedCustomer={selectedEmailCustomer}
        setSelectedCustomer={setSelectedEmailCustomer}
        customers={customers}
        isLoading={sendingEmail}
        handleSendEmail={sendReportByEmail}
      />
    </Grid>
  );
};

export default GstSaleReport;
